/* eslint-disable max-lines */
import { faker } from '@faker-js/faker';
import { generateFromEnum, generateInitialPaginationApi } from '@ingenico/api-sdk-core/mocks';
import type {
  ApplicationVersionResourceReadV2Api,
  ApplicationVersionResourceWriteV2Api,
  BffApplicationVersionResourceReadV2Api,
  ConfigurationModuleResourceParametersBffV2Api,
  ConfigurationModuleResourceParametersMinimalV2Api,
  ConfigurationModuleResourceParametersV2Api,
  ConfigurationModuleResourceReadBffV2Api,
  ConfigurationModuleResourceReadV2Api,
  ConfigurationResourceOsVersionBffV2Api,
  ConfigurationResourceReadBffV2Api,
  ConfigurationResourceSnapshotApplicationItemBffV2Api,
  ConfigurationResourceSnapshotConfigurationDefinitionDeploymentBffV2Api,
  ConfigurationResourceSnapshotSystemPackageItemBffV2Api,
  DeploymentUnitResourceItemBffV2Api,
  DeploymentUnitResourceReadBffV2Api,
  DeviceChangeResourceLogicalDeviceApi,
  EnumAssetTypeV2Api,
  EnumSoftwareVersionTypeV2Api,
  EnumVersionPublicationStatusApi,
  ErrorApi,
  ErrorWithPlaceholdersApi,
  GetBffApplicationVersionByIdV2QueryResponseApi,
  GetConfigurationByIdBffV2QueryResponseApi,
  GetConfigurationModuleSnapshotByIdBffV2QueryResponseApi,
  GetDeployedComputedConfigurationByDeviceIdBffV2QueryResponseApi,
  GetDeploymentUnitByIdBffV2QueryResponseApi,
  ParameterSetFieldValueBffV2Api,
  ParameterSetFieldValueItemSmartFieldBffV2Api,
  ParameterSetFieldValueStructureBffV2Api,
  ParameterSetFieldValueV2Api,
  ParameterSetValueItemBffV2Api,
  ParameterSetValueItemV2Api,
  ParentEntityApi,
  PhysicalDeviceResourceApi,
  PhysicalDeviceResourceReadApi,
  PhysicalDeviceSerialNumberApi,
  PlaceholderKvApi,
  SearchDeploymentUnitsBffV2MutationResponseApi,
} from '@ingenico/api-sdk-device-management-private-v2';
import {
  enumConfigurationResourceSoftwareItemV2,
  enumDeploymentUnitStatusV2,
  enumManufacturer,
  enumParameterSetFieldValueTypeV2,
  enumReferenceModeV2,
  provisioningEntityTypeEnum,
} from '@ingenico/api-sdk-device-management-private-v2';
import type { PathParams, ResponseResolverReturnType, HttpRequestResolverExtras, ResponseResolverInfo } from 'msw';
import { HttpResponse } from 'msw';
import { getApplicationVersionByIdV2InitialData } from './application-versions';
import { DEVICE_DETAILS_PARAMETER_SETS } from './commonUuids';
import {
  getFormGeneratorIdByIndex,
  getParameterSetIdsByIndex,
  getTemplateIdByIndex,
  NUMBER_FORM_GENERATOR_FIELDS,
  NUMBER_TEMPLATES_PARAMETER_SETS,
} from './commonUuids';

export function getBffApplicationVersionByIdV2Mock(): BffApplicationVersionResourceReadV2Api {
  return {
    applicationDependencyVersionIds: [faker.string.uuid()],
    applicationId: faker.string.uuid(),
    artifacts: generateArtifacts(),
    compatibleOsVersionIds: [faker.string.uuid()],
    creationDate: faker.date.past(),
    id: faker.string.uuid(),
    longDescription: 'Release notes',
    publisherId: faker.string.uuid(),
    shortDescription: faker.lorem.words(2),
    size: faker.number.int({ min: 10, max: 100 }),
    status: 'PUBLISHED',
    systemPackageDependencyVersionIds: [faker.string.uuid()],
    templates: [
      {
        artifactId: faker.string.uuid(),
        targetLocation: faker.system.filePath(),
      },
    ],
    versionNumber: faker.system.semver(),
    applicationDependencyVersions: [
      {
        name: faker.lorem.word(),
        versionId: faker.string.uuid(),
        versionNumber: faker.system.semver(),
      },
    ],
    compatibleOsVersions: [
      {
        name: faker.lorem.word(),
        versionId: faker.string.uuid(),
        versionNumber: faker.system.semver(),
      },
    ],
    systemPackageDependencyVersions: [
      {
        name: faker.lorem.word(),
        versionId: faker.string.uuid(),
        versionNumber: faker.system.semver(),
      },
    ],
  };
}

export async function putApplicationVersionByIdV2Handler(
  context: ResponseResolverInfo<HttpRequestResolverExtras<PathParams>>,
): Promise<ResponseResolverReturnType<ApplicationVersionResourceReadV2Api>> {
  const data = (await context.request.json()) as ApplicationVersionResourceWriteV2Api;
  const pathVersionId = context.params.id ?? context.params.applicationVersionId;
  const applicationVersionId = Array.isArray(pathVersionId)
    ? (pathVersionId[0] ?? faker.string.uuid())
    : (pathVersionId ?? faker.string.uuid());

  return HttpResponse.json<ApplicationVersionResourceReadV2Api>({
    applicationDependencyVersionIds: data?.applicationDependencyVersionIds,
    applicationId: data?.applicationId,
    artifacts: generateArtifacts(),
    compatibleOsVersionIds: data?.compatibleOsVersionIds,
    creationDate: faker.date.recent(),
    id: applicationVersionId,
    longDescription: data?.longDescription,
    publisherId: faker.string.uuid(),
    shortDescription: data?.shortDescription,
    size: faker.number.int({ min: 1000, max: 100000 }),
    status: data?.status,
    systemPackageDependencyVersionIds: data?.systemPackageDependencyVersionIds,
    templates: data?.templates,
    versionNumber: data?.versionNumber,
  });
}

// eslint-disable-next-line max-lines-per-function
export function getBffConfigurationByIdV2InitialData(): GetConfigurationByIdBffV2QueryResponseApi {
  return {
    creationDate: faker.date.past(),
    creator: {
      id: faker.string.uuid(),
      name: faker.lorem.word(),
    },
    id: faker.string.uuid(),
    updateDate: faker.date.past(),
    updatedBy: {
      id: faker.string.uuid(),
      name: faker.lorem.word(),
    },
    definition: {
      compositions: [{ configurationId: faker.string.uuid(), name: faker.lorem.word() }],
      configuration: {
        applicationVersions: [
          {
            id: faker.string.uuid(),
            order: 1,
            type: 'SOFTWARE_VERSION_NO_PARAM',
            applicationId: faker.string.uuid(),
            applicationLogo: {
              id: faker.string.uuid(),
              type: 'IMAGE',
            },
            applicationVersions: [],
            applicationVersionStatus: 'DEPRECATED',
            applicationStatus: 'DEPRECATED',
            applicationName: faker.lorem.word(),
            applicationVersionId: faker.string.uuid(),
            applicationVersionNumber: '1.0.0',
            configurationModuleName: faker.lorem.word(),
          },
          {
            id: faker.string.uuid(),
            order: 0,
            type: enumConfigurationResourceSoftwareItemV2.CONFIGURATION_MODULE,
            applicationId: faker.string.uuid(),
            applicationLogo: {
              id: faker.string.uuid(),
              type: 'IMAGE',
            },
            applicationVersions: [],
            applicationVersionStatus: 'DEPRECATED',
            applicationStatus: 'DEPRECATED',
            applicationName: faker.lorem.word(),
            applicationVersionId: faker.string.uuid(),
            applicationVersionNumber: '1.0.0',
            configurationModuleName: faker.lorem.word(),
          },
        ],
        osVersion: {
          id: faker.string.uuid(),
          osVersionStatus: 'PUBLISHED',
          osStatus: 'PUBLISHED',
          osVersions: [],
          osId: 'simpleId',
          osLogo: {
            id: faker.string.uuid(),
            type: 'IMAGE',
          },
          osName: faker.lorem.word(),
          osVersionId: faker.string.uuid(),
          osVersionNumber: '1.0.0',
        },
        remoteKeyInjection: [faker.string.uuid(), faker.string.uuid()],
        systemPackageVersions: [
          {
            id: faker.string.uuid(),
            order: 1,
            type: 'SOFTWARE_VERSION_NO_PARAM',
            configurationModuleName: faker.lorem.word(),
            systemPackageId: faker.string.uuid(),
            systemPackageLogo: {
              id: faker.string.uuid(),
              type: 'IMAGE',
            },
            systemPackageVersions: [],
            systemPackageVersionStatus: 'DEPRECATED',
            systemPackageStatus: 'DEPRECATED',
            systemPackageName: faker.lorem.word(),
            systemPackageVersionId: faker.string.uuid(),
            systemPackageVersionNumber: '1.0.0',
          },
        ],
      },
    },
    description: faker.lorem.words(2),
    name: faker.lorem.word(),
  };
}

function generateArtifacts(): ApplicationVersionResourceReadV2Api['artifacts'] {
  const artifactsCount = faker.number.int({ min: 1, max: 4 });

  return Array(artifactsCount)
    .fill(undefined)
    .map((_, index) => ({
      id: faker.string.uuid(),
      order: index,
    })) satisfies ApplicationVersionResourceReadV2Api['artifacts'];
}

function generateUpdatedByInfo() {
  return {
    id: faker.string.uuid(),
    name: faker.commerce.product(),
  };
}

function generateApplicationVersion(
  override?: Partial<ConfigurationResourceSnapshotApplicationItemBffV2Api>,
): ConfigurationResourceSnapshotApplicationItemBffV2Api {
  const applicationVersionId = faker.string.uuid();
  const applicationVersionNumber = faker.system.semver();
  const applicationVersionStatus = 'PUBLISHED';
  return {
    configurationModuleSnapshotId: faker.string.uuid(),
    id: faker.string.uuid(),
    order: faker.number.int({ max: 10_000 }),
    type: faker.helpers.arrayElement(Object.values(enumConfigurationResourceSoftwareItemV2)),
    applicationId: faker.string.uuid(),
    applicationLogo: {
      id: faker.string.uuid(),
      type: 'LOGO',
    },
    applicationVersions: [
      {
        versionId: applicationVersionId,
        versionNumber: applicationVersionNumber,
        versionStatus: applicationVersionStatus,
      },
    ],
    applicationVersionStatus,
    applicationStatus: 'PUBLISHED',
    applicationName: faker.commerce.product(),
    applicationVersionId,
    applicationVersionNumber,
    configurationModuleName: faker.commerce.product(),
    ...override,
  };
}

function generateSystemPackageVersion(
  override: Partial<ConfigurationResourceSnapshotSystemPackageItemBffV2Api>,
): ConfigurationResourceSnapshotSystemPackageItemBffV2Api {
  const systemPackageVersionId = faker.string.uuid();
  const systemPackageVersionNumber = faker.system.semver();
  const systemPackageVersionStatus = 'PUBLISHED';
  return {
    configurationModuleSnapshotId: faker.string.uuid(),
    id: faker.string.uuid(),
    order: faker.number.int({ max: 10_000 }),
    type: faker.helpers.arrayElement(Object.values(enumConfigurationResourceSoftwareItemV2)),
    configurationModuleName: faker.commerce.product(),
    systemPackageId: faker.string.uuid(),
    systemPackageLogo: {
      id: faker.string.uuid(),
      type: 'LOGO',
    },
    systemPackageVersionStatus,
    systemPackageStatus: 'PUBLISHED',
    systemPackageVersions: [
      {
        versionId: systemPackageVersionId,
        versionNumber: systemPackageVersionNumber,
        versionStatus: systemPackageVersionStatus,
      },
    ],
    systemPackageName: faker.commerce.product(),
    systemPackageVersionId,
    systemPackageVersionNumber,
    ...override,
  };
}

function generateOsVersion(): ConfigurationResourceOsVersionBffV2Api {
  const osVersionId = faker.string.uuid();
  const osVersionNumber = faker.system.semver();
  const osVersionStatus = 'PUBLISHED';
  return {
    id: faker.string.uuid(),
    osId: faker.string.uuid(),
    osLogo: {
      id: faker.string.uuid(),
      type: 'LOGO',
    },
    osVersions: [
      {
        versionId: osVersionId,
        versionNumber: osVersionNumber,
        versionStatus: osVersionStatus,
      },
    ],
    osVersionStatus,
    osStatus: 'PUBLISHED',
    osName: faker.commerce.product(),
    osVersionId,
    osVersionNumber,
  };
}

export function getConfigurationSnapshotByIdBffV2ResponseMock(): ConfigurationResourceReadBffV2Api {
  return {
    creationDate: faker.date.past(),
    updatedBy: generateUpdatedByInfo(),
    id: faker.string.uuid(),
    name: faker.commerce.product(),
    creator: generateUpdatedByInfo(),
    updateDate: faker.date.past(),
    description: faker.lorem.sentence(),
    definition: {
      compositions: [
        {
          configurationId: faker.string.uuid(),
          name: faker.lorem.word(),
        },
      ],
      configuration: {
        applicationVersions: faker.helpers.multiple(
          (_, index) =>
            generateApplicationVersion({
              type:
                index % 3
                  ? enumConfigurationResourceSoftwareItemV2.SOFTWARE_VERSION_NO_PARAM
                  : enumConfigurationResourceSoftwareItemV2.CONFIGURATION_MODULE,
            }),
          { count: { min: 5, max: 70 } },
        ),
        systemPackageVersions: faker.helpers.multiple(
          (_, index) =>
            generateSystemPackageVersion({
              type:
                index % 3
                  ? enumConfigurationResourceSoftwareItemV2.SOFTWARE_VERSION_NO_PARAM
                  : enumConfigurationResourceSoftwareItemV2.CONFIGURATION_MODULE,
            }),
          { count: { min: 5, max: 70 } },
        ),
        osVersion: generateOsVersion(),
        remoteKeyInjection: [faker.string.uuid(), faker.string.uuid()],
      },
    },
  };
}

// eslint-disable-next-line max-lines-per-function
export function generateDeploymentUnitResourceItemBffV2(): DeploymentUnitResourceItemBffV2Api {
  return {
    creationDate: faker.date.past(),
    deploymentRequestId: faker.string.uuid(),
    description: faker.lorem.sentence(),
    deviceId: faker.string.uuid(),
    endDate: faker.date.future(),
    id: faker.string.uuid(),
    logicalDevice: { id: faker.string.uuid(), name: faker.commerce.product(), reference: faker.string.uuid() },
    merchantId: faker.string.uuid(),
    merchantName: faker.commerce.product(),
    name: faker.commerce.product(),
    options: {
      configuration: {
        snapshotId: faker.string.uuid(),
        type: 'CUSTOM',
      },
    },
    physicalDevice: {
      id: faker.string.uuid(),
      manufacturer: 'ingenico',
      model: 'Q92',
      partNumber: '1234567890',
      serialNumber: '1234564523',
      isBlocked: false,
    },
    startDate: faker.date.past(),
    status: generateFromEnum(enumDeploymentUnitStatusV2),
    statusDetail: {
      errorCode: faker.helpers.arrayElement([
        'CON-ERR-001',
        'CON-ERR-002',
        'CON-ERR-003',
        'CON-ERR-004',
        'CON-ERR-005',
        'CON-ERR-006',
        'CON-ERR-007',
        'CON-ERR-008',
        'CON-ERR-009',
        'CON-ERR-010',
        'CON-ERR-011',
        'CON-ERR-012',
        'CON-ERR-013',
        'CON-ERR-014',
        'CON-ERR-015',
        'CON-ERR-016',
        'CON-ERR-017',
        'CON-ERR-018',
        'CON-ERR-019',
        'CON-ERR-020',
        'CON-ERR-021',
        'CON-ERR-022',
        'CON-ERR-023',
        'CON-ERR-024',
        'CON-ERR-025',
        'CON-ERR-026',
        'CON-ERR-10',
        'CON-ERR-17',
        'CON-ERR-31',
        'CON-ERR-401',
        'CON-ERR-403',
        'DPB-ERR-002',
        'DPB-ERR-003',
        'DPB-ERR-004',
        'DPB-ERR-005',
        'DPB-ERR-006',
        'DPB-ERR-007',
        'DPB-ERR-008',
        'DPB-ERR-009',
        'DPB-ERR-010',
        'DPB-ERR-011',
        'DPB-ERR-012',
        'DPB-ERR-013',
        'DPB-ERR-014',
        'DPB-ERR-015',
        'DPB-ERR-016',
        'DPB-ERR-017',
        'DPB-ERR-018',
        'DPB-ERR-019',
        'DPB-ERR-020',
        'DPB-ERR-021',
        'DPB-ERR-022',
        'DPB-ERR-023',
        'DPB-ERR-024',
        'DPB-ERR-025',
        'DPB-ERR-401',
        'DPB-ERR-403',
      ]),
      message: faker.commerce.product(),
      placeholders: [
        generatePlaceholderKvApi(),
        { key: 'charCount', value: 'charCountValue' },
        { key: 'configProfileName', value: 'configProfileNameValue' },
        { key: 'endDate', value: 'endDateValue' },
        { key: 'fieldName', value: 'fieldNameValue' },
        { key: 'fieldType', value: 'fieldTypeValue' },
        { key: 'format', value: 'formatValue' },
        { key: 'max', value: 'maxValue' },
        { key: 'maxLength', value: 'maxLengthValue' },
        { key: 'maxRows', value: 'maxRowsValue' },
        { key: 'smartFieldName', value: 'smartFieldNameValue' },
        { key: 'min', value: 'minValue' },
        { key: 'minLength', value: 'minLengthValue' },
        { key: 'minRows', value: 'minRowsValue' },
        { key: 'pattern', value: 'patternValue' },
        { key: 'refFieldLabel', value: 'refFieldLabelValue' },
        { key: 'refFieldName', value: 'refFieldNameValue' },
        { key: 'rowCount', value: 'rowCountValue' },
        { key: 'startDate', value: 'startDateValue' },
        { key: 'value', value: 'valueValue' },
      ],
    },
  };
}

export function searchDeploymentUnitsBffV2CustomHandler() {
  const count = faker.number.int({ min: 50, max: 100 });

  const configurationModules: SearchDeploymentUnitsBffV2MutationResponseApi = {
    pagination: generateInitialPaginationApi(count),
    data: faker.helpers.multiple(generateDeploymentUnitResourceItemBffV2, { count }),
  };

  return HttpResponse.json<SearchDeploymentUnitsBffV2MutationResponseApi>(configurationModules);
}
export function getBffApplicationVersionByIdV2InitialData(): GetBffApplicationVersionByIdV2QueryResponseApi {
  return {
    ...getApplicationVersionByIdV2InitialData(),
    applicationDependencyVersions: [],
    compatibleOsVersions: [],
    systemPackageDependencyVersions: [],
  };
}

// eslint-disable-next-line max-lines-per-function
export function getConfigurationModuleByIdBffV2CustomHandler() {
  return HttpResponse.json<ConfigurationModuleResourceReadBffV2Api>({
    createdBy: {
      id: 'd0e8e8e0-1b4d-4336-99b8-548e08b05801',
      name: 'John',
    },
    creationDate: new Date('2021-01-01T00:00:00Z'),
    description: 'description',
    id: 'd0e8e8e0-1b4d-4336-99b8-548e08b05801',
    name: 'ConfigurationModuleName',
    softwareVersion: {
      id: '8c37dbbb-fee4-4a0b-89a1-24983f5c1fFF',
      softwareId: 'd0e8e8e0-1b4d-4336-99b8-548e08b05801',
      softwareName: 'PayPal',
      softwareVersionNumber: '1.0.0',
      type: 'APPLICATION_VERSION',
      softwareLogo: {
        id: 'd4db8ac1-703b-4839-8d10-86a0d23fd4ac',
        type: 'LOGO',
      },
      softwareVersionStatus: 'PUBLISHED',
      softwareStatus: 'PUBLISHED',
    },
    updatedBy: {
      id: 'd0e8e8e0-1b4d-4336-99b8-548e08b05801',
      name: 'John',
    },
    updatedDate: new Date('2021-01-01T00:00:00Z'),
    parameterSets: [
      {
        id: 'd0e8e9e0-1b4d-4336-99b8-548e08b05801',
        templateId: 'd0e8e8e0-1b4d-4336-99b8-548e08b05801',
        values: [
          {
            fieldId: 'Parameter_1',
            fieldValue: {
              smartField: {
                id: '53cb5f38-f301-490c-9d3b-c3211d7ebee2',
              },
              type: 'SINGLE',
            },
          },
          {
            fieldId: 'Parameter_2',
            fieldValue: {
              single: '2',
              type: 'SINGLE',
            },
          },
          {
            fieldId: 'Services',
            fieldValue: {
              structure: [
                {
                  cells: [
                    {
                      columnId: 'service_name',
                      fieldValue: {
                        single: 'Service name example 1',
                        type: 'SINGLE',
                      },
                    },
                    {
                      columnId: 'service_type',
                      fieldValue: {
                        single: 'CUSTOMER_PRESENT',
                        type: 'SINGLE',
                      },
                    },
                  ],
                  rowId: '1',
                },
                {
                  cells: [
                    {
                      columnId: 'service_name',
                      fieldValue: {
                        single: 'Service name example 2',
                        type: 'SINGLE',
                      },
                    },
                    {
                      columnId: 'service_type',
                      fieldValue: {
                        single: 'CUSTOMER_PRESENT',
                        type: 'SINGLE',
                      },
                    },
                  ],
                  rowId: '2',
                },
              ],
              type: 'STRUCTURE',
            },
          },
          {
            fieldId: 'EcrConnectionConfig',
            fieldValue: {
              structure: [
                {
                  cells: [
                    {
                      columnId: 'connection_name',
                      fieldValue: {
                        single: 'Connection name value',
                        type: 'SINGLE',
                      },
                    },
                    {
                      columnId: 'RefECRLink',
                      fieldValue: {
                        structure: [
                          {
                            cells: [
                              {
                                columnId: 'test_name_1',
                                fieldValue: {
                                  single: 'Test name 1 value',
                                  type: 'SINGLE',
                                },
                              },
                            ],
                            rowId: '1',
                          },
                          {
                            cells: [
                              {
                                columnId: 'test_name_2',
                                fieldValue: {
                                  single: 'Test name 2 value',
                                  type: 'SINGLE',
                                },
                              },
                            ],
                            rowId: '2',
                          },
                        ],
                        type: 'STRUCTURE',
                      },
                    },
                  ],
                  rowId: '1',
                },
                {
                  cells: [
                    {
                      columnId: 'connection_name',
                      fieldValue: {
                        single: 'Connection name value',
                        type: 'SINGLE',
                      },
                    },
                    {
                      columnId: 'RefECRLink',
                      fieldValue: {
                        structure: [
                          {
                            cells: [
                              {
                                columnId: 'test_name_1',
                                fieldValue: {
                                  single: 'Test name 1 value',
                                  type: 'SINGLE',
                                },
                              },
                            ],
                            rowId: '1',
                          },
                          {
                            cells: [
                              {
                                columnId: 'test_name_2',
                                fieldValue: {
                                  single: 'Test name 2 value',
                                  type: 'SINGLE',
                                },
                              },
                            ],
                            rowId: '2',
                          },
                        ],
                        type: 'STRUCTURE',
                      },
                    },
                  ],
                  rowId: '2',
                },
              ],
              type: 'STRUCTURE',
            },
          },
          {
            fieldId: 'service_choices',
            fieldValue: {
              single: 'CHOICE_3;CHOICE_4',
              type: 'SINGLE',
            },
          },
          {
            fieldId: 'service_toggle',
            fieldValue: {
              single: 'true',
              type: 'SINGLE',
            },
          },
          {
            fieldId: 'service_date_time',
            fieldValue: {
              single: '2025-05-18T05:00:00Z',
              type: 'SINGLE',
            },
          },
        ],
      },
    ],
  });
}

// eslint-disable-next-line max-lines-per-function
export function getConfigurationModuleByIdBffV2CustomHandlerForDetailsPage() {
  return HttpResponse.json<ConfigurationModuleResourceReadBffV2Api>({
    createdBy: {
      id: 'd0e8e8e0-1b4d-4336-99b8-548e08b05801',
      name: 'John',
    },
    creationDate: new Date('2021-01-01T00:00:00Z'),
    description: 'description',
    id: 'd0e8e8e0-1b4d-4336-99b8-548e08b05801',
    name: 'ConfigurationModuleName',
    softwareVersion: {
      softwareId: 'd0e8e8e0-1b4d-4336-99b8-548e08b05801',
      id: 'd0e8e8e0-1b4d-4336-99b8-548e08b05802',
      softwareName: 'PayPal',
      softwareVersionNumber: '1.0.0',
      type: 'APPLICATION_VERSION',
      softwareStatus: 'PUBLISHED',
      softwareVersionStatus: 'PUBLISHED',
      softwareLogo: {
        id: 'd4db8ac1-703b-4839-8d10-86a0d23fd4ac',
        type: 'LOGO',
      },
    },
    updatedBy: {
      id: 'd0e8e8e0-1b4d-4336-99b8-548e08b05801',
      name: 'John',
    },
    updatedDate: new Date('2021-01-01T00:00:00Z'),
    parameterSets: [
      {
        id: DEVICE_DETAILS_PARAMETER_SETS[0].parameterSetId,
        templateId: DEVICE_DETAILS_PARAMETER_SETS[0].templateId,
        values: [
          {
            fieldId: 'Parameter_2',
            fieldValue: {
              single: '2',
              type: 'SINGLE',
            },
          },
          {
            fieldId: 'Services',
            fieldValue: {
              structure: [
                {
                  cells: [
                    {
                      columnId: 'service_name',
                      fieldValue: {
                        single: 'Service name example 1',
                        type: 'SINGLE',
                      },
                    },
                    {
                      columnId: 'service_type',
                      fieldValue: {
                        single: 'CUSTOMER_PRESENT',
                        type: 'SINGLE',
                      },
                    },
                  ],
                  rowId: '1',
                },
                {
                  cells: [
                    {
                      columnId: 'service_name',
                      fieldValue: {
                        single: 'Service name example 2',
                        type: 'SINGLE',
                      },
                    },
                    {
                      columnId: 'service_type',
                      fieldValue: {
                        single: 'CUSTOMER_PRESENT',
                        type: 'SINGLE',
                      },
                    },
                  ],
                  rowId: '2',
                },
              ],
              type: 'STRUCTURE',
            },
          },
          {
            fieldId: 'EcrConnectionConfig',
            fieldValue: {
              structure: [
                {
                  cells: [
                    {
                      columnId: 'connection_name',
                      fieldValue: {
                        single: 'Connection name value',
                        type: 'SINGLE',
                      },
                    },
                    {
                      columnId: 'RefECRLink',
                      fieldValue: {
                        structure: [
                          {
                            cells: [
                              {
                                columnId: 'test_name_1',
                                fieldValue: {
                                  single: 'LINK1',
                                  type: 'SINGLE',
                                },
                              },
                            ],
                            rowId: 'RefECRLink-row-1',
                          },
                          {
                            cells: [
                              {
                                columnId: 'test_name_1',
                                fieldValue: {
                                  single: 'LINK2',
                                  type: 'SINGLE',
                                },
                              },
                              {
                                columnId: 'test_name_2',
                                fieldValue: {
                                  single: 'Test name 2 value',
                                  type: 'SINGLE',
                                },
                              },
                            ],
                            rowId: 'RefECRLink-row-2',
                          },
                        ],
                        type: 'STRUCTURE',
                      },
                    },
                  ],
                  rowId: 'RowEcrConnectionConfig1',
                },
              ],
              type: 'STRUCTURE',
            },
          },
          {
            fieldId: 'service_choices',
            fieldValue: {
              single: '["CHOICE_3"]',
              type: 'SINGLE',
            },
          },
          {
            fieldId: 'service_toggle',
            fieldValue: {
              single: 'true',
              type: 'SINGLE',
            },
          },
          {
            fieldId: 'service_date_time',
            fieldValue: {
              single: '2025-05-18T05:00:00Z',
              type: 'SINGLE',
            },
          },
        ],
      },
      {
        id: DEVICE_DETAILS_PARAMETER_SETS[1].parameterSetId,
        templateId: DEVICE_DETAILS_PARAMETER_SETS[1].templateId,
        values: [
          {
            fieldId: 'service_self_managed',
            fieldValue: {
              smartField: {
                id: '7403dfbb-3792-4f5b-90e4-6b891fe0f069',
                name: 'alter officiis',
              },
              type: 'SMART_FIELD',
            },
          },
          {
            fieldId: 'Parameter_Number',
            fieldValue: {
              single: '4',
              type: 'SINGLE',
            },
          },
          {
            fieldId: 'service_enabled',
            fieldValue: {
              single: 'false',
              type: 'SINGLE',
            },
          },
          /*{
            fieldId: 'service_name',
            fieldValue: {
              single: 'Ingenico Service',
              type: 'SINGLE',
            },
          },*/
        ],
      },
    ],
  });
}

export function generatePlaceholderKvApi(placeholder?: Partial<PlaceholderKvApi>): PlaceholderKvApi {
  return {
    key: 'something',
    value: 'somethingElse',
    ...placeholder,
  };
}
export function generateErrorApi(error?: Partial<ErrorApi>): ErrorApi {
  return {
    details: 'Details Of Error',
    instance: {
      pointer: 'a pointer of the error',
    },
    title: 'title Of Error',
    type: 'CON-ERR-019',
    ...error,
  };
}
export function generateErrorWithPlaceholdersApi(error?: Partial<ErrorWithPlaceholdersApi>): ErrorWithPlaceholdersApi {
  return {
    ...generateErrorApi(),
    placeholders: [
      generatePlaceholderKvApi(),
      { key: 'charCount', value: 'charCountValue' },
      { key: 'configProfileName', value: 'configProfileNameValue' },
      { key: 'endDate', value: 'endDateValue' },
      { key: 'fieldName', value: 'fieldNameValue' },
      { key: 'fieldType', value: 'fieldTypeValue' },
      { key: 'format', value: 'formatValue' },
      { key: 'max', value: 'maxValue' },
      { key: 'maxLength', value: 'maxLengthValue' },
      { key: 'maxRows', value: 'maxRowsValue' },
      { key: 'min', value: 'minValue' },
      { key: 'minLength', value: 'minLengthValue' },
      { key: 'minRows', value: 'minRowsValue' },
      { key: 'pattern', value: 'patternValue' },
      { key: 'refFieldLabel', value: 'refFieldLabelValue' },
      { key: 'refFieldName', value: 'refFieldNameValue' },
      { key: 'rowCount', value: 'rowCountValue' },
      { key: 'startDate', value: 'startDateValue' },
      { key: 'value', value: 'valueValue' },
    ],
    ...error,
  };
}

export function deleteConfigurationModuleByIdErrorError() {
  return HttpResponse.json(generateErrorWithPlaceholdersApi(), { status: 404 });
}

export function deleteConfigurationModuleByIdV2CustomHandler() {
  return new HttpResponse(null, { status: 204 });
}

export function getDeployedComputedConfigurationByDeviceIdBffV2ResponseMock(): GetDeployedComputedConfigurationByDeviceIdBffV2QueryResponseApi {
  return generateConfigurationResourceComputedReadBffV2();
}

// eslint-disable-next-line max-lines-per-function
export function generateConfigurationResourceComputedReadBffV2(): ConfigurationResourceSnapshotConfigurationDefinitionDeploymentBffV2Api {
  return {
    applicationVersions: [
      {
        applicationId: faker.string.uuid(),
        deploymentUnitId: faker.string.uuid(),
        applicationLogo: {
          id: faker.string.uuid(),
          type: 'LOGO',
        },
        applicationName: faker.commerce.product(),
        configurationModuleName: faker.commerce.product(),
        id: faker.string.uuid(),
        order: faker.number.int({ min: 1000, max: 5000 }),
        type: 'CONFIGURATION_MODULE',
        applicationVersionId: faker.string.uuid(),
        applicationStatus: 'PUBLISHED',
        applicationVersionNumber: '1.0.1',
        applicationVersions: [],
        applicationVersionStatus: 'PUBLISHED',
      },
    ],
    systemPackageVersions: [
      {
        deploymentUnitId: faker.string.uuid(),
        systemPackageId: faker.string.uuid(),
        id: faker.string.uuid(),
        order: faker.number.int({ min: 1000, max: 5000 }),
        systemPackageLogo: {
          id: faker.string.uuid(),
          type: 'LOGO',
        },
        systemPackageName: faker.company.name(),
        systemPackageStatus: 'DEPRECATED',
        systemPackageVersionId: faker.string.uuid(),
        systemPackageVersionNumber: faker.system.semver(),
        systemPackageVersions: [
          {
            versionId: faker.string.uuid(),
            versionNumber: faker.system.semver(),
            versionStatus: 'PUBLISHED',
          },
        ],
        systemPackageVersionStatus: 'PUBLISHED',
        type: 'CONFIGURATION_MODULE',
        configurationModuleName: faker.commerce.product(),
        configurationModuleSnapshotId: faker.string.uuid(),
      },
    ],
    remoteKeyInjection: [faker.string.uuid(), faker.string.uuid()],
    osVersion: {
      deploymentUnitId: faker.string.uuid(),
      id: faker.string.uuid(),
      osId: faker.string.uuid(),
      osName: faker.commerce.product(),
      osVersionId: faker.string.uuid(),
      osVersionNumber: '1.0.1',
      osLogo: {
        id: faker.string.uuid(),
        type: 'LOGO',
      },
      osStatus: 'PUBLISHED',
      osVersions: [],
      osVersionStatus: 'PUBLISHED',
    },
  };
}

function generateDeviceChangeResourceLogicalDeviceApi(): DeviceChangeResourceLogicalDeviceApi {
  return {
    id: faker.string.uuid(),
    name: faker.commerce.product(),
    reference: faker.lorem.paragraph(),
  };
}
function generatePhysicalDeviceSerialNumberApi(): PhysicalDeviceSerialNumberApi {
  return faker.string.uuid();
}
function generateParentEntityApi(): ParentEntityApi {
  return {
    id: faker.string.uuid(),
    type: generateFromEnum(provisioningEntityTypeEnum),
  };
}

function generatePhysicalDeviceResourceApi(): PhysicalDeviceResourceApi {
  return {
    id: faker.string.uuid(),
    manufacturer: generateFromEnum(enumManufacturer),
    model: 'Q92',
    parent: generateParentEntityApi(),
    partNumber: faker.commerce.product(),
    reference: faker.commerce.product(),
    serialNumber: generatePhysicalDeviceSerialNumberApi(),
  };
}

function generatePhysicalDeviceResourceReadApi(): PhysicalDeviceResourceReadApi {
  return {
    ...generatePhysicalDeviceResourceApi(),
    isBlocked: faker.datatype.boolean(),
  };
}

// eslint-disable-next-line max-lines-per-function
function generateDeploymentUnitResourceBffV2Base(): DeploymentUnitResourceItemBffV2Api {
  return {
    deploymentRequestId: faker.string.uuid(),
    description: faker.lorem.sentence(),
    deviceId: faker.string.uuid(),
    endDate: faker.date.past(),
    id: faker.string.uuid(),
    name: faker.lorem.word(),
    startDate: faker.date.past(),
    status: generateFromEnum(enumDeploymentUnitStatusV2),
    creationDate: faker.date.past(),
    logicalDevice: generateDeviceChangeResourceLogicalDeviceApi(),
    merchantId: faker.string.uuid(),
    merchantName: faker.commerce.product(),
    physicalDevice: generatePhysicalDeviceResourceReadApi(),
    options: {
      configuration: {
        snapshotId: faker.string.uuid(),
        type: generateFromEnum(enumReferenceModeV2),
      },
      delete: {
        applicationVersionsIds: [],
        filePaths: [],
        packageIds: [],
        systemPackageVersionsIds: [],
      },
    },
    statusDetail: {
      errorCode: faker.helpers.arrayElement([
        'CON-ERR-001',
        'CON-ERR-002',
        'CON-ERR-003',
        'CON-ERR-004',
        'CON-ERR-005',
        'CON-ERR-006',
        'CON-ERR-007',
        'CON-ERR-008',
        'CON-ERR-009',
        'CON-ERR-010',
        'CON-ERR-011',
        'CON-ERR-012',
        'CON-ERR-013',
        'CON-ERR-014',
        'CON-ERR-015',
        'CON-ERR-016',
        'CON-ERR-017',
        'CON-ERR-018',
        'CON-ERR-019',
        'CON-ERR-020',
        'CON-ERR-021',
        'CON-ERR-022',
        'CON-ERR-023',
        'CON-ERR-024',
        'CON-ERR-025',
        'CON-ERR-026',
        'CON-ERR-10',
        'CON-ERR-17',
        'CON-ERR-31',
        'CON-ERR-401',
        'CON-ERR-403',
        'DPB-ERR-002',
        'DPB-ERR-003',
        'DPB-ERR-004',
        'DPB-ERR-005',
        'DPB-ERR-006',
        'DPB-ERR-007',
        'DPB-ERR-008',
        'DPB-ERR-009',
        'DPB-ERR-010',
        'DPB-ERR-011',
        'DPB-ERR-012',
        'DPB-ERR-013',
        'DPB-ERR-014',
        'DPB-ERR-015',
        'DPB-ERR-016',
        'DPB-ERR-017',
        'DPB-ERR-018',
        'DPB-ERR-019',
        'DPB-ERR-020',
        'DPB-ERR-021',
        'DPB-ERR-022',
        'DPB-ERR-023',
        'DPB-ERR-024',
        'DPB-ERR-025',
        'DPB-ERR-401',
        'DPB-ERR-403',
      ]),
      message: faker.commerce.product(),
      placeholders: [
        generatePlaceholderKvApi(),
        { key: 'charCount', value: 'charCountValue' },
        { key: 'configProfileName', value: 'configProfileNameValue' },
        { key: 'endDate', value: 'endDateValue' },
        { key: 'fieldName', value: 'fieldNameValue' },
        { key: 'fieldType', value: 'fieldTypeValue' },
        { key: 'format', value: 'formatValue' },
        { key: 'max', value: 'maxValue' },
        { key: 'maxLength', value: 'maxLengthValue' },
        { key: 'maxRows', value: 'maxRowsValue' },
        { key: 'min', value: 'minValue' },
        { key: 'minLength', value: 'minLengthValue' },
        { key: 'minRows', value: 'minRowsValue' },
        { key: 'pattern', value: 'patternValue' },
        { key: 'smartFieldName', value: 'smartFieldNameValue' },
        { key: 'refFieldLabel', value: 'refFieldLabelValue' },
        { key: 'refFieldName', value: 'refFieldNameValue' },
        { key: 'rowCount', value: 'rowCountValue' },
        { key: 'startDate', value: 'startDateValue' },
        { key: 'value', value: 'valueValue' },
      ],
    },
  };
}

function generateDeploymentUnitResourceReadBffV2Api(): DeploymentUnitResourceReadBffV2Api {
  return generateDeploymentUnitResourceBffV2Base() as unknown as DeploymentUnitResourceReadBffV2Api;
}

export function generateDeploymentUnitResourceReadBffV2(): DeploymentUnitResourceReadBffV2Api {
  return generateDeploymentUnitResourceReadBffV2Api();
}

export function getDeploymentUnitByIdBffV2ResponseMock(): GetDeploymentUnitByIdBffV2QueryResponseApi {
  return generateDeploymentUnitResourceReadBffV2();
}
function generateParameterSetFieldValueStructureBffV2Api(): ParameterSetFieldValueStructureBffV2Api {
  return [];
}
function generateParameterSetFieldValueItemSmartFieldBffV2Api(): ParameterSetFieldValueItemSmartFieldBffV2Api {
  return {
    id: faker.string.uuid(),
    name: faker.person.fullName(),
  };
}
function generateParameterSetFieldValueBffV2Api(
  override?: Partial<ParameterSetFieldValueBffV2Api>,
): ParameterSetFieldValueBffV2Api {
  return {
    single: faker.finance.accountName(),
    type: generateFromEnum(enumParameterSetFieldValueTypeV2),
    smartField: generateParameterSetFieldValueItemSmartFieldBffV2Api(),
    structure: generateParameterSetFieldValueStructureBffV2Api(),
    ...override,
  };
}

export function generateParameterSetValueItemBffV2Api(
  override?: Partial<ParameterSetValueItemBffV2Api>,
): ParameterSetValueItemBffV2Api {
  return {
    fieldId: faker.string.uuid(),
    fieldValue: generateParameterSetFieldValueBffV2Api(),
    ...override,
  };
}

export function generateConfigurationModuleResourceParametersMinimalV2Api(
  override?: Partial<ConfigurationModuleResourceParametersMinimalV2Api>,
): ConfigurationModuleResourceParametersMinimalV2Api {
  return {
    id: faker.string.uuid(),
    ...override,
  };
}

export function generateConfigurationModuleResourceParametersBffV2Api(
  override?: Partial<ConfigurationModuleResourceParametersBffV2Api>,
): ConfigurationModuleResourceParametersBffV2Api {
  return {
    ...generateConfigurationModuleResourceParametersMinimalV2Api(override),
    templateId: faker.string.uuid(),
    values: faker.helpers.multiple(() => generateParameterSetValueItemBffV2Api(), { count: 10 }),
    ...override,
  };
}

export function generateConfigurationModuleByIdBffV2(
  override?: Partial<ConfigurationModuleResourceReadBffV2Api>,
): ConfigurationModuleResourceReadBffV2Api {
  return {
    createdBy: {
      id: faker.string.uuid(),
      name: faker.commerce.product(),
    },
    creationDate: faker.date.past(),
    updatedBy: {
      id: faker.string.uuid(),
      name: faker.commerce.product(),
    },
    updatedDate: faker.date.recent(),
    id: faker.string.uuid(),
    name: faker.commerce.product(),
    parameterSets: faker.helpers.multiple(() => generateConfigurationModuleResourceParametersBffV2Api(), { count: 4 }),
    softwareVersion: {
      softwareId: faker.string.uuid(),
      id: faker.string.uuid(),
      softwareName: faker.commerce.product(),
      softwareVersionNumber: '1.0.1',
      type: 'APPLICATION_VERSION',
      softwareLogo: {
        id: faker.string.uuid(),
        type: 'LOGO',
      },
      softwareStatus: 'PUBLISHED',
      softwareVersionStatus: 'PUBLISHED',
    },
    ...override,
  };
}

export function getConfigurationModuleSnapshotByIdBffV2ResponseMock(): GetConfigurationModuleSnapshotByIdBffV2QueryResponseApi {
  return generateConfigurationModuleByIdBffV2();
}

export function getSpecificTemplateIdsConfigurationModuleSnapshotByIdBffV2ResponseMock(): GetConfigurationModuleSnapshotByIdBffV2QueryResponseApi {
  const numberOfTemplates = 2;
  return generateConfigurationModuleByIdBffV2({
    parameterSets: faker.helpers.multiple(
      (_, index) =>
        generateConfigurationModuleResourceParametersBffV2Api({
          templateId: getTemplateIdByIndex(index),
          id: getParameterSetIdsByIndex(index),
          values: faker.helpers.multiple(
            (___, secondIndex) =>
              generateParameterSetValueItemBffV2Api({
                fieldId: getFormGeneratorIdByIndex(secondIndex),
                fieldValue: generateParameterSetFieldValueBffV2Api({
                  // type: enumParameterSetFieldValueTypeV2.SINGLE,
                  type: enumParameterSetFieldValueTypeV2.SMART_FIELD,
                  structure: [],
                }),
              }),
            { count: NUMBER_FORM_GENERATOR_FIELDS },
          ),
        }),
      { count: numberOfTemplates },
    ),
  });
}

export function generateParameterSetFieldValueV2Api(
  override?: Partial<ParameterSetFieldValueV2Api>,
): ParameterSetFieldValueV2Api {
  return {
    ...(generateParameterSetFieldValueBffV2Api(
      override as ParameterSetFieldValueBffV2Api,
    ) as ParameterSetFieldValueV2Api),
    ...override,
  };
}
export function generateParameterSetValueItemV2Api(
  override?: Partial<ParameterSetValueItemV2Api>,
): ParameterSetValueItemV2Api {
  return {
    fieldId: faker.string.uuid(),
    fieldValue: generateParameterSetFieldValueV2Api(),
    ...override,
  };
}

export function generateConfigurationModuleResourceParametersV2Api(
  override?: Partial<ConfigurationModuleResourceParametersV2Api>,
): ConfigurationModuleResourceParametersV2Api {
  return {
    ...generateConfigurationModuleResourceParametersMinimalV2Api(),
    templateId: faker.string.uuid(),
    values: faker.helpers.multiple(() => generateParameterSetValueItemV2Api(), { count: 4 }),
    ...override,
  };
}
export function generateConfigurationModuleResourceReadV2Api(
  override?: Partial<ConfigurationModuleResourceReadV2Api>,
): ConfigurationModuleResourceReadV2Api {
  return {
    createdBy: faker.string.uuid(),
    creationDate: faker.date.past(),
    updatedBy: faker.string.uuid(),
    updatedDate: faker.date.recent(),
    id: faker.string.uuid(),
    name: faker.commerce.product(),
    parameterSets: faker.helpers.multiple(() => generateConfigurationModuleResourceParametersV2Api(), { count: 4 }),
    softwareVersion: {
      id: faker.string.uuid(),
      type: 'APPLICATION_VERSION',
    },
    ...override,
  };
}
export function getSpecificFormBuilderForGetComputedConfigurationModuleSnapshotByIdV2(): ConfigurationModuleResourceReadV2Api {
  const numberOfTemplates = NUMBER_TEMPLATES_PARAMETER_SETS;
  return generateConfigurationModuleResourceReadV2Api({
    parameterSets: faker.helpers.multiple(
      (_, index) =>
        generateConfigurationModuleResourceParametersV2Api({
          templateId: getTemplateIdByIndex(index),
          id: getParameterSetIdsByIndex(index),
          values: faker.helpers.multiple(
            (___, secondIndex) =>
              generateParameterSetValueItemV2Api({
                fieldId: getFormGeneratorIdByIndex(secondIndex),
                fieldValue: generateParameterSetFieldValueV2Api({
                  type: enumParameterSetFieldValueTypeV2.SMART_FIELD,
                  structure: [],
                }),
              }),
            { count: NUMBER_FORM_GENERATOR_FIELDS },
          ),
        }),
      { count: numberOfTemplates },
    ),
  });
}

// MOCK UP OF REAL API CALLS

//Configuration profile v2:

const configurationProfileV2: GetConfigurationModuleSnapshotByIdBffV2QueryResponseApi = {
  creationDate: new Date('2025-06-18T12:33:42.902733Z'),
  id: '4bfb837a-df19-483b-bdf3-8cfb389325f7',
  name: 'Pascal-config-module',
  updatedDate: new Date('2025-06-22T08:59:41.275593Z'),
  createdBy: {
    id: 'eb7c1ba7-99d5-479c-808e-47383ee1180e',
    name: 'Hichem Bousnina',
  },
  softwareVersion: {
    id: 'fb768d90-3761-442e-827d-cf2721498c6b',
    softwareId: 'f77bb156-2e66-467c-8215-66f4c0603049',
    softwareLogo: {
      id: 'c5b4a4f3-5579-4a0f-b8e6-4f8491600ad3',
      type: 'LOGO' as EnumAssetTypeV2Api,
    },
    softwareName: 'Live Payments App',
    softwareStatus: 'PUBLISHED' as EnumVersionPublicationStatusApi,
    softwareVersionNumber: '1.4.62',
    softwareVersionStatus: 'PUBLISHED' as EnumVersionPublicationStatusApi,
    type: 'APPLICATION_VERSION' as EnumSoftwareVersionTypeV2Api,
  },
  updatedBy: {
    id: 'c249c460-e682-4a3c-80c0-90c4b451b260',
    name: 'Pascal Rondeu',
  },
  parameterSets: [
    {
      id: 'fee14517-c314-4517-8340-d034bddaa7f6',
      templateId: '5feb665e-039b-4cd4-bf8e-290a75a623ee',
      values: [
        {
          fieldId: 'network_timeout_period',
          fieldValue: {
            smartField: {
              id: '045a6132-57dd-4844-9a03-f94300d4acd7',
              name: 'LP-common-terminal-network-timeout',
            },
            type: 'SMART_FIELD',
          },
        },
        {
          fieldId: 'Service',
          fieldValue: {
            smartField: {
              id: '2bb833bd-0c86-4793-933a-d3def2e82d72',
              name: 'LP-common-services-services',
            },
            type: 'SMART_FIELD',
          },
        },
        {
          fieldId: 'icc_retry_counter',
          fieldValue: {
            single: '3',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'preauth_code_validated_by_host',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'navi_bar_status',
          fieldValue: {
            single: 'Ignored',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'tmk_value',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'key_system',
          fieldValue: {
            single: 'DUKPT',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'tmk_location',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'idle_screen_timeout',
          fieldValue: {
            single: '60',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'config_fingerprint',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'onlinepin_block_format',
          fieldValue: {
            single: 'BLOCK_FORMAT_0',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'idle_file_name',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'tmk_kcv',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'country_code',
          fieldValue: {
            smartField: {
              id: '15084024-bfcd-47cd-b06b-d134001ace7b',
              name: 'FrenchDevice',
            },
            type: 'SMART_FIELD',
          },
        },
        {
          fieldId: 'delay_pin_block',
          fieldValue: {
            single: 'true',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'pin_mat_supported',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'magstripe_retry_counter',
          fieldValue: {
            single: '3',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'touch_sounds',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'power_button',
          fieldValue: {
            single: 'None',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'pin_retry_counter',
          fieldValue: {
            single: '3',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'dim_enabled',
          fieldValue: {
            single: 'true',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'cless_retry_counter',
          fieldValue: {
            single: '3',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'key_algorithm',
          fieldValue: {
            single: 'TDES',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'enable_qr_scan',
          fieldValue: {
            single: 'DISABLED',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'allow_contactless_fall_forward',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'search_card_timeout',
          fieldValue: {
            single: '30',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'enable_manual_entry',
          fieldValue: {
            single: 'true',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'visually_impaired_mode',
          fieldValue: {
            single: 'true',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'alternative_payment_method_enable',
          fieldValue: {
            single: 'true',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'package_name_to_open',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'transaction_result_screen_timeout',
          fieldValue: {
            single: '10000',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'captive_url_http',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'display_transaction_result_screen',
          fieldValue: {
            single: 'true',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'preauth_code_validated_by_terminal',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'captive_url_https',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'screen_signature_enabled',
          fieldValue: {
            single: 'true',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'signature_timeout',
          fieldValue: {
            single: '30',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'signature_timeout_action',
          fieldValue: {
            single: 'Approve',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'auto_close_app_open_other',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'country_flag_icon',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'mksk_ik_location',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'dukpt_ik_location',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'TerminalUserManagement',
          fieldValue: {
            structure: [],
            type: 'STRUCTURE',
          },
        },
        {
          fieldId: 'TerminalOperation',
          fieldValue: {
            structure: [
              {
                rowId: '1',
                cells: [
                  {
                    columnId: 'operation_name',
                    fieldValue: {
                      single: 'DEBIT',
                      type: 'SINGLE',
                    },
                  },
                  {
                    columnId: 'operation_type',
                    fieldValue: {
                      single: 'debit',
                      smartField: {
                        id: '25084024-bfcd-47cd-b06b-d134001ace7b',
                        name: 'FrenchDevice',
                      },
                      type: 'SMART_FIELD',
                    },
                  },
                ],
              },
              {
                rowId: '2',
                cells: [
                  {
                    columnId: 'operation_name',
                    fieldValue: {
                      single: 'CREDIT',
                      type: 'SINGLE',
                    },
                  },
                  {
                    columnId: 'operation_type',
                    // fieldValue: {
                    //
                    // single: 'credit',
                    // type: 'SINGLE',
                    // },
                    fieldValue: {
                      single: 'debit',
                      smartField: {
                        id: '35084024-bfcd-47cd-b06b-d134001ace7b',
                        name: 'FrenchDevice',
                      },
                      type: 'SMART_FIELD',
                    },
                  },
                ],
              },
              {
                rowId: '3',
                cells: [
                  {
                    columnId: 'operation_name',
                    fieldValue: {
                      single: 'MOTO',
                      type: 'SINGLE',
                    },
                  },
                  {
                    columnId: 'operation_type',
                    // fieldValue: {
                    //   single: 'moto',
                    //   type: 'SINGLE',
                    // },
                    fieldValue: {
                      single: 'moto',
                      smartField: {
                        id: '45084024-bfcd-47cd-b06b-d134001ace7b',
                        name: 'FrenchDevice',
                      },
                      type: 'SMART_FIELD',
                    },
                  },
                ],
              },
              {
                rowId: '4',
                cells: [
                  {
                    columnId: 'operation_type',
                    // fieldValue: {
                    //   single: 'FORCED',
                    //   type: 'SINGLE',
                    // },
                    fieldValue: {
                      single: 'FORCED',
                      smartField: {
                        id: '55084024-bfcd-47cd-b06b-d134001ace7b',
                        name: 'FrenchDevice',
                      },
                      type: 'SMART_FIELD',
                    },
                  },
                  {
                    columnId: 'operation_name',
                    fieldValue: {
                      single: 'FORCED TRANSACTION',
                      type: 'SINGLE',
                    },
                  },
                ],
              },
            ],
            type: 'STRUCTURE',
          },
        },
        {
          fieldId: 'OpenAPIHostConfig',
          fieldValue: {
            structure: [
              {
                rowId: '1',
                cells: [
                  {
                    columnId: 'host_name',
                    fieldValue: {
                      single: 'test',
                      type: 'SINGLE',
                    },
                  },
                  {
                    columnId: 'RefCommsConfig',
                    fieldValue: {
                      structure: [
                        {
                          rowId: '1',
                          cells: [
                            {
                              columnId: 'comms',
                              fieldValue: {
                                single: 'toto',
                                type: 'SINGLE',
                              },
                            },
                          ],
                        },
                      ],
                      type: 'STRUCTURE',
                    },
                  },
                ],
              },
            ],
            type: 'STRUCTURE',
          },
        },
        {
          fieldId: 'IsoHostConfig',
          fieldValue: {
            structure: [
              {
                rowId: '1',
                cells: [
                  {
                    columnId: 'host_name',
                    fieldValue: {
                      single: 'test',
                      type: 'SINGLE',
                    },
                  },
                  {
                    columnId: 'RefCommsConfig',
                    fieldValue: {
                      structure: [
                        {
                          rowId: '1',
                          cells: [
                            {
                              columnId: 'comms',
                              fieldValue: {
                                single: 'test',
                                type: 'SINGLE',
                              },
                            },
                          ],
                        },
                      ],
                      type: 'STRUCTURE',
                    },
                  },
                ],
              },
              {
                rowId: '2',
                cells: [
                  {
                    columnId: 'best_rate_curr_guarantee_disclaimer',
                    fieldValue: {
                      single: 'My best rate is here',
                      type: 'SINGLE',
                    },
                  },
                  {
                    columnId: 'host_name',
                    fieldValue: {
                      single: 'hostname2',
                      type: 'SINGLE',
                    },
                  },
                  {
                    columnId: 'currency_conversion_provider',
                    fieldValue: {
                      single: 'current.provider.value',
                      type: 'SINGLE',
                    },
                  },
                  {
                    columnId: 'RefCommsConfig',
                    fieldValue: {
                      structure: [
                        {
                          rowId: '1',
                          cells: [
                            {
                              columnId: 'comms',
                              fieldValue: {
                                single: 'comms.config.value.1',
                                type: 'SINGLE',
                              },
                            },
                          ],
                        },
                        {
                          rowId: '2',
                          cells: [
                            {
                              columnId: 'comms',
                              fieldValue: {
                                single: 'comms.config.value.2',
                                type: 'SINGLE',
                              },
                            },
                          ],
                        },
                      ],
                      type: 'STRUCTURE',
                    },
                  },
                ],
              },
            ],
            type: 'STRUCTURE',
          },
        },
        {
          fieldId: 'CommsConfig',
          fieldValue: {
            structure: [
              {
                rowId: '1',
                cells: [
                  {
                    columnId: 'comms_policy',
                    fieldValue: {
                      single: 'SEQUENTIAL',
                      type: 'SINGLE',
                    },
                  },
                  {
                    columnId: 'comms_name',
                    fieldValue: {
                      single: 'comms name 1',
                      type: 'SINGLE',
                    },
                  },
                  {
                    columnId: 'tls_profile',
                    fieldValue: {
                      single: 'TLS 1',
                      type: 'SINGLE',
                    },
                  },
                  {
                    columnId: 'RefLinks',
                    fieldValue: {
                      structure: [
                        {
                          rowId: '1',
                          cells: [
                            {
                              columnId: 'link_label',
                              fieldValue: {
                                single: 'link1',
                                type: 'SINGLE',
                              },
                            },
                          ],
                        },
                        {
                          rowId: '2',
                          cells: [
                            {
                              columnId: 'link_label',
                              fieldValue: {
                                single: 'link 2',
                                type: 'SINGLE',
                              },
                            },
                          ],
                        },
                      ],
                      type: 'STRUCTURE',
                    },
                  },
                ],
              },
              {
                rowId: '2',
                cells: [
                  {
                    columnId: 'comms_policy',
                    fieldValue: {
                      single: 'RANDOM',
                      type: 'SINGLE',
                    },
                  },
                  {
                    columnId: 'tls_profile',
                    fieldValue: {
                      single: 'TLS 2',
                      type: 'SINGLE',
                    },
                  },
                  {
                    columnId: 'comms_name',
                    fieldValue: {
                      single: 'comms name 2',
                      type: 'SINGLE',
                    },
                  },
                  {
                    columnId: 'RefLinks',
                    fieldValue: {
                      structure: [
                        {
                          rowId: '1',
                          cells: [
                            {
                              columnId: 'link_label',
                              fieldValue: {
                                single: 'link 2.1',
                                type: 'SINGLE',
                              },
                            },
                          ],
                        },
                        {
                          rowId: '2',
                          cells: [
                            {
                              columnId: 'link_label',
                              fieldValue: {
                                single: 'link 2.2',
                                type: 'SINGLE',
                              },
                            },
                          ],
                        },
                      ],
                      type: 'STRUCTURE',
                    },
                  },
                ],
              },
            ],
            type: 'STRUCTURE',
          },
        },
      ],
    },
    {
      id: '38307fea-c4db-461e-a0be-3b651a119831',
      templateId: '898e3e70-684e-481d-9b0c-c47ed2c87b33',
      values: [
        {
          fieldId: 'referral_password',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'ending_shift_on_settlement',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'period_time_in_efb',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'print_transaction_after_pre_settle',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'contactless_reference_text_entry',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'merchant_address_1',
          fieldValue: {
            single: 'Chennai',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'piggyback_txn_limit',
          fieldValue: {
            single: '3',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'rrn_generation_method',
          fieldValue: {
            single: 'RRN Method 1 - Default Digits Only',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'preset_1',
          fieldValue: {
            single: '5%',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'receipt_advert_message',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'tear_off_receipt_screen',
          fieldValue: {
            single: 'true',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'allow_offline_technical_fallback',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'gratuity_prompt',
          fieldValue: {
            single: 'Gratuity',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'custom_enable',
          fieldValue: {
            single: 'true',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'trickle_feed_minute',
          fieldValue: {
            single: '10',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'saf_retry_limit',
          fieldValue: {
            single: '3',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'status_banner_bar',
          fieldValue: {
            single: 'true',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'reference_text_length',
          fieldValue: {
            single: '10',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'tolerance_percentage',
          fieldValue: {
            single: '0',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'verify_mid_step',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'number_preset',
          fieldValue: {
            single: '4',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'transaction_check_digits',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'general_helpdesk_number',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'ereceipt_enabled',
          fieldValue: {
            single: 'true',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'shift_user_enabled',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'auto_eod_hour',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'timeout_pin_entry_vi',
          fieldValue: {
            single: '60',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'skip_acquirer_selection',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'merchant_id',
          fieldValue: {
            single: '1234567890',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'merchant_category_code',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'receipt_advert_4',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'launch_auto_logon',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'allow_offline_in_airplane_mode',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'trickle_feed_txn_limit',
          fieldValue: {
            single: '3',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'storing_max_age',
          fieldValue: {
            single: '2',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'password_level_locked',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'force_process',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'receipt_advert_1',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'sca_enabled',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'reference_text_receipt_label',
          fieldValue: {
            single: 'reference text receipt label',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'config_fingerprint',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'automatic_preauth_process',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'monitor_printing_enabled',
          fieldValue: {
            single: 'true',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'password_entry_keyboard_type',
          fieldValue: {
            single: 'ALPHANUMERIC',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'piggyback_enabled',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'tip_user_enable',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'auto_start_shift',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'create_supervisor_code_step',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'merchant_company_name',
          fieldValue: {
            single: 'Altimetrik',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'surcharge_prompt_mag',
          fieldValue: {
            single: 'true',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'terminal_id_mask',
          fieldValue: {
            single: 'XXXXXXNN',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'offline_processing_enabled',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'receipt_advert_3',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'merchant_id_mask',
          fieldValue: {
            single: 'XXXXXXXXXXXNNNN',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'cancel_pop_up',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'receipt_courtesy_message',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'surcharge_prompt_contact',
          fieldValue: {
            single: 'true',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'surcharge_min_threshold',
          fieldValue: {
            single: '0',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'dcc_enabled',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'gratuity_mode',
          fieldValue: {
            single: '3',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'receipt_advert_2',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'reference_text_prompt',
          fieldValue: {
            single: 'reference text prompt',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'void_enable',
          fieldValue: {
            single: 'true',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'reporting_server_enabled',
          fieldValue: {
            single: 'true',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'preset_2',
          fieldValue: {
            single: '10%',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'merchant_address_2',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'surcharge_prompt_contactless',
          fieldValue: {
            single: 'true',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'shift_enabled',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'preset_3',
          fieldValue: {
            single: '15%',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'auto_eod_enabled',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'trickle_feed_enable',
          fieldValue: {
            single: 'true',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'merchant_name',
          fieldValue: {
            single: '',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'auth_code_keyboard_type',
          fieldValue: {
            single: 'NUMERIC',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'power_failure_action',
          fieldValue: {
            single: 'Resume',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'surcharging_enable',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'dcc_contactless_enabled',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'sca_single_tap_enabled',
          fieldValue: {
            single: 'false',
            type: 'SINGLE',
          },
        },
        {
          fieldId: 'ForceTransaction',
          fieldValue: {
            structure: [
              {
                rowId: '1',
                cells: [
                  {
                    columnId: 'transaction_name',
                    fieldValue: {
                      single: 'PRE_AUTH_COMP',
                      type: 'SINGLE',
                    },
                  },
                ],
              },
              {
                rowId: '2',
                cells: [
                  {
                    columnId: 'transaction_name',
                    fieldValue: {
                      single: 'SALE',
                      type: 'SINGLE',
                    },
                  },
                ],
              },
            ],
            type: 'STRUCTURE',
          },
        },
        {
          fieldId: 'SupportedService',
          fieldValue: {
            structure: [
              {
                rowId: '1',
                cells: [
                  {
                    columnId: 'service_name',
                    fieldValue: {
                      single: 'myService',
                      type: 'SINGLE',
                    },
                  },
                ],
              },
            ],
            type: 'STRUCTURE',
          },
        },
        {
          fieldId: 'Acquirer',
          fieldValue: {
            structure: [
              {
                rowId: '1',
                cells: [
                  {
                    columnId: 'last_settle_online_mode_enabled',
                    fieldValue: {
                      single: 'true',
                      type: 'SINGLE',
                    },
                  },
                  {
                    columnId: 'pre_connect_enable',
                    fieldValue: {
                      single: 'true',
                      type: 'SINGLE',
                    },
                  },
                  {
                    columnId: 'acquirer_name',
                    fieldValue: {
                      single: 'amex',
                      type: 'SINGLE',
                    },
                  },
                  {
                    columnId: 'host_forced_settlement_enabled',
                    fieldValue: {
                      single: 'true',
                      type: 'SINGLE',
                    },
                  },
                ],
              },
            ],
            type: 'STRUCTURE',
          },
        },
      ],
    },
  ],
};

export function getMockupOfComputedConfigurationModuleSnapshotByIdV2(): GetConfigurationModuleSnapshotByIdBffV2QueryResponseApi {
  return configurationProfileV2;
}
